import { useEffect, useState } from "react";
import { Interval } from "luxon";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CallCenterCurrentState } from "../../Models/CallCenter/CallCenterCallState";
import { IconClassNameCall, LocalizationCallStatus } from "./CallCenter.types";

import styles from "./CallCenterCallStatus.module.scss";

interface CallCenterCallStatusProps {
    callState: CallCenterCurrentState;
    callStart?: Date;
    callEnd?: Date;
    showDuration: boolean
}

function CallCenterCallStatus(props: Readonly<CallCenterCallStatusProps>): JSX.Element {
    const { getText } = useTranslation();
    const [durationText, setDurationText] = useState<string>(null);

    function getLocalizationCallStatusFromCallState(callstate: CallCenterCurrentState): string {
        switch (callstate) {
            case CallCenterCurrentState.Idle:
                return LocalizationCallStatus.Inactive;
            case CallCenterCurrentState.InCall:
                return LocalizationCallStatus.Active;
            case CallCenterCurrentState.OnHold:
                return LocalizationCallStatus.OnHold;
            default:
                return LocalizationCallStatus.Inactive;
        }
    }

    function getClassNameFromCallState(callstate: CallCenterCurrentState): string {
        switch (callstate) {
            case CallCenterCurrentState.Idle:
                return IconClassNameCall.Inactive;
            case CallCenterCurrentState.InCall:
                return IconClassNameCall.Active;
            case CallCenterCurrentState.OnHold:
                return IconClassNameCall.OnHold;
            default:
                return IconClassNameCall.Inactive;
        }
    }

    useEffect(() => {
        if (!props.showDuration || props.callStart == null) {
            setDurationText(null);
            return;
        }

        function updateDurationText(start: Date, end: Date): void {
            const startDate = LuxonDateTimeParser.parse(start);
            let endDate = LuxonDateTimeParser.parse(end);
            // don't allow a negative call time (if time sync is slightly off between user and server)
            if (endDate < startDate) {
                endDate = startDate;
            }

            const callDuration = Interval.fromDateTimes(startDate, endDate).toDuration();

            const text = callDuration.toFormat("hh:mm:ss");

            setDurationText(text);
        }

        if (props.callStart != null && props.callEnd != null) {
            updateDurationText(props.callStart, props.callEnd);
            return;
        }

        function onInterval(): void {
            updateDurationText(props.callStart, new Date());
        }

        onInterval();
        const interval = setInterval(onInterval, 1000);

        return () => clearInterval(interval);
    }, [props.callStart, props.callEnd, props.showDuration]);

    const callStateLocalization: string = getLocalizationCallStatusFromCallState(props.callState);
    const circleCss: string = getClassNameFromCallState(props.callState);

    return (
        <div className={styles.header}>
            <span className={styles.callStatus}>
                {getText("CallCenter_CallStatusLabel")}: <span className={styles.callStatusValue}>{getText(callStateLocalization)}</span>
            </span>
            <span className={styles[circleCss]} />
            {durationText != null && <span className={styles.callDuration}>{durationText}</span>}
        </div>
    );
}

export { CallCenterCallStatus };
